import { useEffect, useMemo, useState } from 'react';
import { ConnectionIndicator, FilterChip } from '@/components/ui';
import { useActivities } from '../../hooks/useActivities';
import { useMeSpaces } from '../../hooks/useMeSpaces';
import { ActivityFeed } from '@/components/activity/ActivityFeed';
import type { ActivityScope } from '../../types';

const PRIORITY_OPTIONS: { value: string; label: string }[] = [
  { value: '__all__', label: 'All priorities' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function ActivityPage() {
  const [scope, setScope] = useState<ActivityScope>('spaces');
  const [forbiddenNotice, setForbiddenNotice] = useState<string | null>(null);

  const {
    activities,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    realtimeCount,
    isConnected,
    diagnostics,
    error,
  } = useActivities(scope);

  const { data: spacesData } = useMeSpaces();
  const spaces = useMemo(() => {
    const items = spacesData?.items ?? [];
    return [...items].sort((a, b) => {
      const persA = a.kind === 'personal' ? 0 : 1;
      const persB = b.kind === 'personal' ? 0 : 1;
      if (persA !== persB) return persA - persB;
      return a.name.localeCompare(b.name);
    });
  }, [spacesData]);

  const activeSpace = scope.startsWith('spc-')
    ? spaces.find((s) => s.spaceId === scope)
    : undefined;

  // If the caller selected a space they're no longer a member of, the
  // backend returns 403 — revert to 'spaces' and surface a notice.
  useEffect(() => {
    const status = (error as { status?: number } | null)?.status;
    if (status === 403 && scope.startsWith('spc-')) {
      setForbiddenNotice("You're no longer a member of that space. Reverted to All spaces.");
      setScope('spaces');
    }
  }, [error, scope]);

  function handleScopeChange(next: string) {
    setForbiddenNotice(null);
    setScope(next as ActivityScope);
  }

  const [priorityFilter, setPriorityFilter] = useState('__all__');

  const filtered = useMemo(() => {
    return activities.filter((a) => {
      if (priorityFilter !== '__all__' && a.priority !== priorityFilter) return false;
      return true;
    });
  }, [activities, priorityFilter]);

  const subtitle =
    scope === 'mine'
      ? 'Activity by you'
      : activeSpace
        ? `Activity in ${activeSpace.name}`
        : 'Monitor activity across your organization';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Activity</h1>
        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
      </div>

      {forbiddenNotice && (
        <div
          role="status"
          className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
        >
          {forbiddenNotice}
        </div>
      )}

      {/* Scope chip row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <FilterChip
          active={scope === 'spaces'}
          onClick={() => handleScopeChange('spaces')}
        >
          All spaces
        </FilterChip>
        <FilterChip
          active={scope === 'mine'}
          onClick={() => handleScopeChange('mine')}
        >
          By me
        </FilterChip>
        {spaces.length > 0 && (
          <span className="text-muted-foreground/50 mx-1 select-none">·</span>
        )}
        {spaces.map((s) => (
          <FilterChip
            key={s.spaceId}
            active={scope === s.spaceId}
            onClick={() => handleScopeChange(s.spaceId)}
          >
            {s.name}
          </FilterChip>
        ))}
      </div>

      {/* Priority chip row (secondary — muted styling) */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {PRIORITY_OPTIONS.map((opt) => (
          <PriorityChip
            key={opt.value}
            active={priorityFilter === opt.value}
            onClick={() => setPriorityFilter(opt.value)}
          >
            {opt.label}
          </PriorityChip>
        ))}
        <ConnectionIndicator isConnected={isConnected} diagnostics={diagnostics} className="ml-auto" />
      </div>

      {/* Count */}
      <p className="text-xs text-muted-foreground mb-3">
        Showing {filtered.length} of {activities.length} activities
        {realtimeCount > 0 && ` (${realtimeCount} new)`}
      </p>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <ActivityFeed
          activities={filtered}
          realtimeCount={realtimeCount}
          isLoading={isLoading}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      </div>
    </div>
  );
}

function PriorityChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] transition-colors ${
        active
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground/70 hover:text-foreground hover:bg-accent/50'
      }`}
    >
      {children}
    </button>
  );
}
